import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Moai } from "../hoa/Moai.js";
import { rangPhase, LIBELLE_PHASE, type Annonceur } from "./api.js";

/* ------------------------------------------------------------------ */
/* Petites pièces                                                      */
/* ------------------------------------------------------------------ */

/** Une explication rangée derrière un « i » : lue au survol, ou au clavier. */
export function Info(props: { texte: string }): JSX.Element {
  return <span className="info" tabIndex={0} role="note" aria-label={props.texte} data-texte={props.texte}>i</span>;
}

export function Point(props: { couleur: string | null; taille?: number }): JSX.Element {
  const t = props.taille ?? 12;
  return <span className="dot" style={{ background: props.couleur ?? "#666666", width: t, height: t, flex: `0 0 ${t}px` }} />;
}

export function Frise(props: { etape: string }): JSX.Element {
  const n = rangPhase(props.etape);
  return (
    <span className="phase">
      <span className="frise4" aria-hidden="true">
        {[1, 2, 3, 4].map((i) => (
          <i key={i} className={i <= n ? "f" : ""} />
        ))}
      </span>
      {LIBELLE_PHASE[props.etape] ?? props.etape}
    </span>
  );
}

export function Kpi(props: { valeur: ReactNode; libelle: string; alerte?: boolean }): JSX.Element {
  return (
    <div className={props.alerte ? "kpi al" : "kpi"}>
      <b>{props.valeur}</b>
      <span>{props.libelle}</span>
    </div>
  );
}

/** Le sélecteur de valeur : même taille partout, aligné à droite de sa ligne. */
export function Pas<T extends number | null>(props: {
  valeurs: readonly T[];
  valeur: T;
  format: (v: T) => string;
  onChange: (v: T) => void;
  libelle: string;
  desactive?: boolean;
}): JSX.Element {
  // La valeur actuelle n'est pas forcément dans la liste (réglée ailleurs) :
  // on se place sur la plus proche.
  let i = props.valeurs.indexOf(props.valeur);
  if (i < 0) {
    const n = props.valeur === null ? Infinity : Number(props.valeur);
    i = props.valeurs.findIndex((v) => (v === null ? Infinity : Number(v)) >= n);
    if (i < 0) i = props.valeurs.length - 1;
  }
  return (
    <span className="pas" role="group" aria-label={props.libelle}>
      <button type="button" aria-label="Moins" disabled={props.desactive || i <= 0} onClick={() => props.onChange(props.valeurs[i - 1]!)}>
        −
      </button>
      <span aria-live="polite">{props.format(props.valeur)}</span>
      <button
        type="button"
        aria-label="Plus"
        disabled={props.desactive || i >= props.valeurs.length - 1}
        onClick={() => props.onChange(props.valeurs[i + 1]!)}
      >
        +
      </button>
    </span>
  );
}

export function Reglage(props: { libelle: string; info?: string; children: ReactNode }): JSX.Element {
  return (
    <div className="reg">
      <span>
        {props.libelle}
        {props.info ? <Info texte={props.info} /> : null}
      </span>
      {props.children}
    </div>
  );
}

export function Interrupteur(props: { valeur: boolean; libelle: string; onChange: (v: boolean) => void }): JSX.Element {
  return (
    <button type="button" role="switch" aria-checked={props.valeur} aria-label={props.libelle} className="interrupteur" onClick={() => props.onChange(!props.valeur)}>
      <i />
    </button>
  );
}

export function Croix(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

export function Retour(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

export function Ico(props: { nom: "soir" | "signal" | "reglages" | "systeme" | "fil" | "voix" | "evenements" | "annonceur" | "externe" }): JSX.Element {
  const p = { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
  switch (props.nom) {
    case "soir":
      return (
        <svg {...p}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case "signal":
      return (
        <svg {...p}>
          <path d="M5 21V4h11l-1.5 4L16 12H5" />
        </svg>
      );
    case "reglages":
      return (
        <svg {...p}>
          <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
          <circle cx="16" cy="7" r="2" />
          <circle cx="10" cy="17" r="2" />
        </svg>
      );
    case "systeme":
      return (
        <svg {...p}>
          <rect x="3" y="4" width="18" height="12" rx="2" />
          <path d="M8 20h8M12 16v4" />
        </svg>
      );
    case "fil":
      return (
        <svg {...p} width={16} height={16}>
          <path d="M3 7h8M7 12h11M5 17h7" />
          <path d="M15 4v16" strokeDasharray="2 3" />
        </svg>
      );
    case "voix":
      return (
        <svg {...p}>
          <path d="M4 10v4h3l6 4V6L7 10H4z" />
          <path d="M17 9a4 4 0 0 1 0 6" />
        </svg>
      );
    case "evenements":
      return (
        <svg {...p}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </svg>
      );
    case "annonceur":
      return (
        <svg {...p}>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21c1-4 4-6 8-6s7 2 8 6" />
        </svg>
      );
    case "externe":
      return (
        <svg {...p} width={16} height={16}>
          <path d="M14 4h6v6M20 4l-9 9M18 14v6H4V6h6" />
        </svg>
      );
  }
}

/** La voix qui parle aux participants : l'annonceur s'il en a une, sinon Hoa. */
export function Voix(props: { annonceur: Annonceur | null; taille?: number }): JSX.Element {
  const a = props.annonceur;
  const t = props.taille ?? 32;
  const style = { width: t, height: t, flex: `0 0 ${t}px`, fontSize: Math.round(t * 0.45) };
  if (a?.image) {
    return (
      <span className="voix" style={style}>
        <img src={a.image} alt="" />
      </span>
    );
  }
  if (a?.nom) {
    return (
      <span className="voix" style={style}>
        {a.nom.trim()[0]?.toUpperCase()}
      </span>
    );
  }
  return (
    <span className="voix moai" style={style}>
      <Moai />
    </span>
  );
}

/** Ce que voit un participant : la voix, et sa bulle. */
export function Apercu(props: { annonceur: Annonceur | null; nom?: boolean; children: ReactNode; suite?: ReactNode }): JSX.Element {
  const a = props.annonceur;
  const parle = a && (a.nom || a.image);
  return (
    <div className="apercu">
      <Voix annonceur={a} />
      <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0, flex: "1 1 auto" }}>
        <div className="bulle">
          {props.nom && parle ? <small>{a!.nom || "L'organisateur"}</small> : null}
          {props.children}
        </div>
        {props.suite}
      </div>
    </div>
  );
}

export function Dialogue(props: { titre: string; onFermer: () => void; children: ReactNode; etroit?: boolean }): JSX.Element {
  useEffect(() => {
    const f = (e: KeyboardEvent): void => {
      if (e.key === "Escape") props.onFermer();
    };
    window.addEventListener("keydown", f);
    return () => window.removeEventListener("keydown", f);
  }, [props]);
  return (
    <>
      <div className="voile haut" onClick={props.onFermer} />
      <section className={props.etroit ? "dialogue etroit" : "dialogue"} role="dialog" aria-modal="true" aria-label={props.titre}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h2 className="h2" style={{ fontSize: 24 }}>
            {props.titre}
          </h2>
          <span className="sp" />
          <button type="button" className="rond" aria-label="Fermer" onClick={props.onFermer}>
            <Croix />
          </button>
        </div>
        {props.children}
      </section>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Crochets                                                            */
/* ------------------------------------------------------------------ */

/** Relire une ressource à intervalle, tant que l'écran est monté. */
export function useLecture<T>(charger: () => Promise<T>, periodeMs: number | null, deps: unknown[] = []): {
  donnees: T | null;
  erreur: string | null;
  relire: () => Promise<void>;
} {
  const [donnees, setDonnees] = useState<T | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const enCours = useRef(false);
  const f = useRef(charger);
  f.current = charger;
  const relire = useCallback(async (): Promise<void> => {
    if (enCours.current) return;
    enCours.current = true;
    try {
      setDonnees(await f.current());
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      enCours.current = false;
    }
  }, []);
  useEffect(() => {
    void relire();
    if (periodeMs === null) return;
    const id = window.setInterval(() => void relire(), periodeMs);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relire, periodeMs, ...deps]);
  return { donnees, erreur, relire };
}

/** Enregistrer après un court silence : chaque changement part, mais pas chaque frappe. */
export function useEnregistrement<T>(envoyer: (v: T) => Promise<void>, delaiMs = 600): (v: T) => void {
  const minuteur = useRef<number | null>(null);
  const f = useRef(envoyer);
  f.current = envoyer;
  useEffect(
    () => () => {
      if (minuteur.current !== null) window.clearTimeout(minuteur.current);
    },
    [],
  );
  return useCallback(
    (v: T) => {
      if (minuteur.current !== null) window.clearTimeout(minuteur.current);
      minuteur.current = window.setTimeout(() => {
        minuteur.current = null;
        void f.current(v);
      }, delaiMs);
    },
    [delaiMs],
  );
}

export function useMaintenant(periodeMs = 1000): number {
  const [n, setN] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setN(Date.now()), periodeMs);
    return () => window.clearInterval(id);
  }, [periodeMs]);
  return n;
}

/* ------------------------------------------------------------------ */
/* Le petit message en bas d'écran                                     */
/* ------------------------------------------------------------------ */

type Dire = (texte: string, erreur?: boolean) => void;
let direGlobal: Dire = () => undefined;
export const dire: Dire = (t, e) => direGlobal(t, e);

export function Messager(): JSX.Element | null {
  const [m, setM] = useState<{ texte: string; erreur: boolean } | null>(null);
  const minuteur = useRef<number | null>(null);
  useEffect(() => {
    direGlobal = (texte, erreur = false) => {
      setM({ texte, erreur });
      if (minuteur.current !== null) window.clearTimeout(minuteur.current);
      minuteur.current = window.setTimeout(() => setM(null), erreur ? 6000 : 3000);
    };
    return () => {
      direGlobal = () => undefined;
    };
  }, []);
  if (!m) return null;
  return (
    <div className={m.erreur ? "message x" : "message"} role="status">
      {m.texte}
    </div>
  );
}

/** Une commande : le message dit ce qui s'est passé, ou pourquoi ça n'a pas marché. */
export async function agir(f: () => Promise<unknown>, reussite?: string): Promise<boolean> {
  try {
    await f();
    if (reussite) dire(reussite);
    return true;
  } catch (e) {
    dire(e instanceof Error ? e.message : String(e), true);
    return false;
  }
}

/* ------------------------------------------------------------------ */
/* Impression (affiche, chevalets)                                     */
/* ------------------------------------------------------------------ */


export function Impression(props: { children: ReactNode; onFini: () => void }): JSX.Element {
  useEffect(() => {
    document.body.classList.add("rg-imprime");
    const fini = (): void => {
      document.body.classList.remove("rg-imprime");
      props.onFini();
    };
    window.addEventListener("afterprint", fini, { once: true });
    const id = window.setTimeout(() => window.print(), 250);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("afterprint", fini);
      document.body.classList.remove("rg-imprime");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return createPortal(<div className="rg-impression">{props.children}</div>, document.body);
}
